import type {
  APIConfig,
  APIError,
  AssistantsResponse,
  Conversation,
  ConversationsResponse,
  CreateConversationRequest,
  Message,
  MessagesResponse,
  SendMessageRequest,
} from './types';

/**
 * Mock DevXGPT API Client
 *
 * Simulates real API responses for development/testing without a backend.
 * Based on actual BFF response samples.
 */
export class MockDevXGPTClient {
  private config: APIConfig;
  private mockConversations: Conversation[] = [];
  private mockMessages: Map<string, Message[]> = new Map();
  private messageIdCounter = 1;

  constructor(config: APIConfig) {
    this.config = config;
    this.initializeMockData();
  }

  /**
   * Initialize with sample data
   */
  private initializeMockData() {
    // Sample conversations from BFF/all-convs
    this.mockConversations = [
      {
        assistant_id: this.config.assistantId || 'asst_7quN17LzIcqSJRDjU7UKzg7p',
        title: 'What is silver layer?',
        conversation_id: '0.wpqe2qnt9n',
        created_at: '2025-10-09T07:43:41.213512Z',
        updated_at: '2025-10-13T08:58:08.621526Z',
      },
      {
        assistant_id: this.config.assistantId || 'asst_6o9jTmQUFo5SXeNAapCHdp0x',
        title: 'How Can I onboard to RI003?',
        conversation_id: '0.wpqe2qns9n',
        created_at: '2025-10-09T07:42:03.417270Z',
        updated_at: '2025-10-09T13:40:36.669141Z',
      },
      {
        assistant_id: this.config.assistantId || 'asst_6o9jTmQUFo5SXeNAapCHdp0x',
        title: 'Help me with DevX development setup',
        conversation_id: '0.blls69aitjj',
        created_at: '2025-09-25T06:10:38.483289Z',
        updated_at: '2025-09-25T08:23:25.927220Z',
      },
    ];

    // Sample messages for a conversation
    this.mockMessages.set('0.wpqe2qns9n', [
      {
        id: 22278,
        conversation_id: '0.wpqe2qns9n',
        role: 'user',
        message: 'How Can I onboard to RI003?',
        team_id: '240bafdd-fb13-4f2f-9b75-db28647be7e4',
        created_at: '2025-10-09T07:42:03.417270Z',
        message_metadata: {
          assistant_id: 'asst_6o9jTmQUFo5SXeNAapCHdp0x',
          thread_id: 'thread_5lylXbvmFC0NbhCM7W1CfaIG',
          external_id: 'asst_6o9jTmQUFo5SXeNAapCHdp0x',
        },
      },
      {
        id: 22279,
        conversation_id: '0.wpqe2qns9n',
        role: 'assistant',
        message:
          'To onboard to the RI003 solution, you can follow these steps according to the documentation:\n\n1. **Login**:\n    - Access the URL: https://devx-infra.mypepsico.com\n    - The system will redirect you to the SSO login page (OKTA).\n    - Complete the Multi-factor authentication (MFA) prompt.\n\n2. **Submit the Order in Cloud Marketplace**:\n    - Go to the "Solutions" tab.\n    - Select the RI003 solution from the available options.\n\nThese steps guide you through the entire onboarding process for RI003.',
        team_id: '240bafdd-fb13-4f2f-9b75-db28647be7e4',
        created_at: '2025-10-09T07:42:03.623556Z',
        message_metadata: {
          assistant_id: 'asst_6o9jTmQUFo5SXeNAapCHdp0x',
          thread_id: 'thread_5lylXbvmFC0NbhCM7W1CfaIG',
          external_id: 'asst_6o9jTmQUFo5SXeNAapCHdp0x',
        },
      },
    ]);
  }

  /**
   * Simulate network delay
   */
  private async delay(ms: number = 300) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async getConversations(assistantId?: string): Promise<ConversationsResponse> {
    await this.delay(500);

    console.log('🎭 Mock: Fetching conversations...');

    return {
      team_id: '240bafdd-fb13-4f2f-9b75-db28647be7e4',
      assistant_id: assistantId || this.config.assistantId || 'asst_6o9jTmQUFo5SXeNAapCHdp0x',
      total_conversations: this.mockConversations.length,
      conversations: this.mockConversations,
      assistants_count: 2,
    };
  }

  /**
   * Get mock messages for a conversation
   */
  async getMessages(conversationId: string): Promise<MessagesResponse> {
    await this.delay(400);

    console.log('🎭 Mock: Fetching messages for conversation:', conversationId);

    const messages = this.mockMessages.get(conversationId) || [];

    return {
      assistant_id: this.config.assistantId || 'asst_6o9jTmQUFo5SXeNAapCHdp0x',
      conversation_id: conversationId,
      messages,
    };
  }

  /**
   * Simulate streaming message response
   */
  // async sendMessageOld(
  //   request: SendMessageRequest,
  //   onChunk: (text: string) => void,
  //   onComplete?: (fullMessage: string) => void,
  //   _onError?: (error: APIError) => void,
  //   signal?: AbortSignal
  // ): Promise<void> {
  //   console.log('🎭 Mock: Sending message:', request.message);

  //   // Check if aborted
  //   if (signal?.aborted) {
  //     throw new Error('Request aborted');
  //   }

  //   await this.delay(800);

  //   // Mock response based on the question
  //   const responses: Record<string, string> = {
  //     hello: "Hi! I'm DevXGPT. How can I help you today?",
  //     hi: 'Hello! Welcome to DevXGPT. What would you like to know?',
  //     help: 'I can help you with:\n\n1. DevX Infrastructure setup\n2. Cloud Marketplace provisioning\n3. RI003 onboarding\n4. Data Foundation questions\n5. Technical documentation\n\nWhat would you like to know more about?',
  //     ri003:
  //       'To onboard to RI003, you need to:\n\n1. **Login** to https://devx-infra.mypepsico.com\n2. **Navigate** to Solutions tab\n3. **Select** RI003 solution\n4. **Fill out** the provisioning form\n5. **Submit** for approval\n\nThe process typically takes 2-3 business days.',
  //     silver:
  //       'The Silver Layer is an intermediate data processing layer responsible for refining and standardizing raw data from the Bronze Layer. It includes:\n\n- Data validation and cleaning\n- Schema enforcement\n- Business rule application\n- Data quality checks\n\nThe Silver Layer prepares data for consumption in the Gold Layer.',
  //     devx: 'DevX Infrastructure provides:\n\n1. **Cloud Marketplace** - Self-service provisioning\n2. **Reference Implementations** - Pre-configured solutions\n3. **Automation** - Infrastructure as Code\n4. **Security** - Built-in compliance\n5. **Support** - 24/7 assistance\n\nWhat specific aspect would you like to explore?',
  //     default: `I understand you're asking about: "${request.message}"\n\nBased on the documentation, here's what I can tell you:\n\nThis is a mock response simulating the real API. The actual API will provide detailed answers based on your specific question and the documentation available.\n\nTo get real responses, please connect to the production API once it's deployed.`,
  //   };

  //   // Find matching response
  //   const messageLower = request.message.toLowerCase();
  //   let response = responses.default;

  //   for (const [key, value] of Object.entries(responses)) {
  //     if (messageLower.includes(key)) {
  //       response = value;
  //       break;
  //     }
  //   }

  //   // Simulate streaming by breaking response into chunks
  //   const words = response.split(' ');
  //   let fullMessage = '';

  //   for (let i = 0; i < words.length; i++) {
  //     // Check if aborted during streaming
  //     if (signal?.aborted) {
  //       throw new Error('Request aborted');
  //     }

  //     const chunk = words[i] + (i < words.length - 1 ? ' ' : '');
  //     fullMessage += chunk;

  //     // Send chunk
  //     onChunk(chunk);

  //     // Simulate typing delay (faster than real API)
  //     await this.delay(100);
  //   }

  //   // Add to mock messages
  //   const conversationId = request.conversation_id || 'mock_conv_' + Date.now();

  //   if (!this.mockMessages.has(conversationId)) {
  //     this.mockMessages.set(conversationId, []);
  //   }

  //   const messages = this.mockMessages.get(conversationId)!;

  //   // Add user message
  //   messages.push({
  //     id: this.messageIdCounter++,
  //     conversation_id: conversationId,
  //     role: 'user',
  //     message: request.message,
  //     created_at: new Date().toISOString(),
  //   });

  //   // Add assistant message
  //   messages.push({
  //     id: this.messageIdCounter++,
  //     conversation_id: conversationId,
  //     role: 'assistant',
  //     message: fullMessage,
  //     created_at: new Date().toISOString(),
  //   });

  //   // Call completion callback
  //   if (onComplete) {
  //     onComplete(fullMessage);
  //   }

  //   console.log('🎭 Mock: Message sent successfully');
  // }

  async sendMessage(
    request: SendMessageRequest,
    onChunk: (msg: { text?: string; files?: any[]; id?: number; eventType?: string }) => void,
    onComplete?: (msg: { text?: string; files?: any[]; conversationId?: string; threadId?: string }) => void,
    _onError?: (error: APIError) => void,
    signal?: AbortSignal
  ): Promise<void> {
    console.log('🎭 Mock: Sending message:', request.message);

    if (signal?.aborted) {
      throw new Error('Request aborted');
    }

    await this.delay(800);

    // Simulate event types and file attachments
    const responses: Record<string, { text: string; files?: any[]; eventType?: string }> = {
      hello: { text: "Hi! I'm DevXGPT. How can I help you today?", eventType: 'thread.message.delta' },
      hi: { text: 'Hello! Welcome to DevXGPT. What would you like to know?', eventType: 'thread.message.delta' },
      help: { text: 'I can help you with:\n\n1. DevX Infrastructure setup\n2. Cloud Marketplace provisioning\n3. RI003 onboarding\n4. Data Foundation questions\n5. Technical documentation\n\nWhat would you like to know more about?', eventType: 'thread.message.delta' },
      ri003: { text: 'To onboard to RI003, you need to:\n\n1. **Login** to https://devx-infra.mypepsico.com\n2. **Navigate** to Solutions tab\n3. **Select** RI003 solution\n4. **Fill out** the provisioning form\n5. **Submit** for approval\n\nThe process typically takes 2-3 business days.', eventType: 'thread.message.delta' },
      silver: { text: 'The Silver Layer is an intermediate data processing layer responsible for refining and standardizing raw data from the Bronze Layer. It includes:\n\n- Data validation and cleaning\n- Schema enforcement\n- Business rule application\n- Data quality checks\n\nThe Silver Layer prepares data for consumption in the Gold Layer.', eventType: 'thread.message.delta' },
      devx: { text: 'DevX Infrastructure provides:\n\n1. **Cloud Marketplace** - Self-service provisioning\n2. **Reference Implementations** - Pre-configured solutions\n3. **Automation** - Infrastructure as Code\n4. **Security** - Built-in compliance\n5. **Support** - 24/7 assistance\n\nWhat specific aspect would you like to explore?', eventType: 'thread.message.delta' },
      file: { text: '', files: [{ file_id: 'mock_file_1', name: 'mock.txt', status: 'saved' }], eventType: 'message.attachment' },
      default: { text: `I understand you're asking about: "${request.message}"\n\nThis is a mock response simulating the real API.`, eventType: 'thread.message.delta' },
    };

    const messageLower = request.message.toLowerCase();
    let responseObj = responses.default;
    for (const [key, value] of Object.entries(responses)) {
      if (messageLower.includes(key)) {
        responseObj = value;
        break;
      }
    }

    // Simulate streaming by breaking response into chunks
    const words = responseObj.text ? responseObj.text.split(' ') : [];
    let fullMessage = '';
    let files: any[] = responseObj.files || [];

    for (let i = 0; i < words.length; i++) {
      if (signal?.aborted) {
        throw new Error('Request aborted');
      }
      const chunk = words[i] + (i < words.length - 1 ? ' ' : '');
      fullMessage += chunk;
      onChunk({ text: chunk, eventType: responseObj.eventType });
      await this.delay(100);
    }

    // Simulate file attachment event
    if (files.length > 0) {
      onChunk({ files, eventType: 'message.attachment' });
    }

    // Add to mock messages
    const conversationId = request.conversation_id || 'mock_conv_' + Date.now();
    if (!this.mockMessages.has(conversationId)) {
      this.mockMessages.set(conversationId, []);
    }
    const messages = this.mockMessages.get(conversationId)!;

    messages.push({
      id: this.messageIdCounter++,
      conversation_id: conversationId,
      role: 'user',
      message: request.message,
      created_at: new Date().toISOString(),
    });

    messages.push({
      id: this.messageIdCounter++,
      conversation_id: conversationId,
      role: 'assistant',
      message: fullMessage,
      created_at: new Date().toISOString(),
    });

    // Simulate completion event
    if (onComplete) {
      onComplete({
        text: fullMessage,
        files,
        conversationId,
        threadId: 'mock_thread_' + conversationId,
      });
    }

    console.log('🎭 Mock: Message sent successfully');
  }

  /**
   * Create a new mock conversation
   */
  async createConversation(
    request: CreateConversationRequest
  ): Promise<{ conversation_id: string }> {
    await this.delay(300);

    const newConv: Conversation = {
      assistant_id: request.assistant_id || this.config.assistantId || 'asst_mock',
      title: request.title || 'New Conversation',
      conversation_id: 'mock_' + Date.now(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    this.mockConversations.unshift(newConv);
    this.mockMessages.set(newConv.conversation_id, []);

    console.log('🎭 Mock: Created conversation:', newConv.conversation_id);

    return { conversation_id: newConv.conversation_id };
  }

  /**
   * Delete a mock conversation
   */
  async deleteConversation(conversationId: string): Promise<void> {
    await this.delay(200);

    this.mockConversations = this.mockConversations.filter(
      (c) => c.conversation_id !== conversationId
    );
    this.mockMessages.delete(conversationId);

    console.log('🎭 Mock: Deleted conversation:', conversationId);
  }

  /**
   * Update mock conversation title
   */
  async updateConversation(conversationId: string, title: string): Promise<void> {
    await this.delay(200);

    const conv = this.mockConversations.find((c) => c.conversation_id === conversationId);
    if (conv) {
      conv.title = title;
      conv.updated_at = new Date().toISOString();
    }

    console.log('🎭 Mock: Updated conversation:', conversationId, 'to:', title);
  }

  /**
   * Mock assistant data - simulates the backend assistants endpoint
   */
  async getAssistants(): Promise<AssistantsResponse> {
    await this.delay(300);

    console.log('🎭 Mock: Fetching assistants...');

    return {
      assistants: [
        {
          title: 'Ask DevX',
          assistant_id: 'asst_6o9jTmQUFo5SXeNAapCHdp0x',
          description: 'DevX Assistant for developer queries and support.',
          prompts: [
            'What are the different RI',
            'How can I onboard application on producer portal?',
            'How can I onboard a new project within the CMP platform?',
          ],
          tags: ['developer', 'onboarding', 'DevX'],
        },
        {
          title: 'Ask EDF',
          assistant_id: 'asst_7quN17LzIcqSJRDjU7UKzg7p',
          description: 'EDF Assistant for enterprise data foundation queries.',
          prompts: [
            'How can I request access to the data foundation?',
            'How do I access the data lake?',
            'What is Gold Layer?',
          ],
          tags: ['edf', 'data', 'enterprise'],
        },
        {
          title: 'Ask Kubernetes',
          assistant_id: 'asst_k8s',
          description: 'Kubernetes Assistant for cluster and deployment questions.',
          prompts: ['List all running pods.', 'How do I scale my deployment?'],
          tags: ['kubernetes', 'deployment', 'devops'],
        },
        {
          title: 'Assistant 404',
          assistant_id: 'asst_404',
          description: 'Assistant cannot be found',
          prompts: ['What is the status of my request?', 'Show me recent activity.'],
          tags: ['misc', 'support'],
        },
      ],
      default_assistant_id: 'asst_6o9jTmQUFo5SXeNAapCHdp0x', // Ask DevX as default
    };
  }
}

/**
 * Create a mock DevXGPT client
 */
export function createMockDevXGPTClient(config: APIConfig): MockDevXGPTClient {
  return new MockDevXGPTClient(config);
}
